/**
 * Currency formatting utilities for internationalization
 */

import { getCountry } from './countries';

/**
 * Get currency code from country ID
 * @param {number} countryId - ISO country code
 * @returns {string} - Currency code (USD, GBP, EUR, etc.)
 */
export const getCurrencyCode = (countryId) => {
    return getCountry(countryId).currency;
};

/**
 * Get locale from country ID
 * @param {number} countryId - ISO country code
 * @returns {string} - Locale string (en-US, en-GB, etc.)
 */
export const getLocale = (countryId) => {
    return getCountry(countryId).locale;
};

/**
 * Format a price with the appropriate currency symbol and locale
 * @param {number} amount - The price amount
 * @param {number} countryId - ISO country code (840=US, 826=UK, 372=Ireland)
 * @param {string} currencyOverride - Optional currency code override
 * @returns {string} - Formatted price string (e.g., "$100,000", "£75,000", "€50,000")
 */
export const formatPrice = (amount, countryId, currencyOverride) => {
    if (amount == null || isNaN(amount)) {
        return null;
    }

    const currency = currencyOverride || getCurrencyCode(countryId);
    const locale = getLocale(countryId);

    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch (e) {
        // Fallback for older browsers
        const symbols = { USD: '$', GBP: '£', EUR: '€', CAD: 'C$' };
        const symbol = symbols[currency] || '$';
        return `${symbol}${amount.toLocaleString()}`;
    }
};

/**
 * Format a price range (e.g., guide price)
 * @param {number} minPrice - Minimum price
 * @param {number} maxPrice - Maximum price (optional)
 * @param {number} countryId - ISO country code
 * @returns {string} - Formatted price range
 */
export const formatPriceRange = (minPrice, maxPrice, countryId) => {
    const formattedMin = formatPrice(minPrice, countryId);

    if (!formattedMin) return null;
    if (!maxPrice || maxPrice === minPrice) return formattedMin;

    const formattedMax = formatPrice(maxPrice, countryId);
    return `${formattedMin} - ${formattedMax}`;
};

/**
 * Check if country uses metric system (for area display)
 * @param {number} countryId - ISO country code
 * @returns {boolean}
 */
export const usesMetric = (countryId) => {
    return getCountry(countryId).useMetric;
};

/**
 * Format area based on country preference
 * @param {number} sqft - Area in square feet
 * @param {number} countryId - ISO country code
 * @returns {string} - Formatted area string
 */
export var formatArea = function(sqft, countryId) {
    if (!sqft) return null;

    // Ensure sqft is a number (API may return string)
    var sqftNum = parseFloat(sqft);
    if (isNaN(sqftNum)) return null;

    if (usesMetric(countryId)) {
        var sqm = Math.round(sqftNum * 0.092903);
        return sqm.toLocaleString() + ' m²';
    }

    return Math.round(sqftNum).toLocaleString() + ' sq ft';
};

/**
 * Format lot size based on country preference
 * @param {number} acres - Lot size in acres
 * @param {number} countryId - ISO country code
 * @returns {string} - Formatted lot size string
 */
export var formatLotSize = function(acres, countryId) {
    if (!acres) return null;

    // Ensure acres is a number (API may return string)
    var acresNum = parseFloat(acres);
    if (isNaN(acresNum)) return null;

    if (usesMetric(countryId)) {
        var hectares = acresNum * 0.404686;
        // Show decimal for small lots (< 10 ha), whole number for large
        var formatted = hectares < 10 ? hectares.toFixed(1) : Math.round(hectares);
        return formatted + ' ha';
    }

    // Show decimal for small lots (< 10 acres), whole number for large
    var acresFormatted = acresNum < 10 ? acresNum.toFixed(1) : Math.round(acresNum);
    return acresFormatted + ' acres';
};
